import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useUploadWakeLock } from '../../../services/upload/useUploadWakeLock';
import { getAttempt, msSinceAttempt, registerAbandonStage, setAttemptMeta } from '../../../services/analytics/telemetry';
import { SLOW_MBPS_THRESHOLD } from '../../../services/upload/slowConnectionPolicy';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAppDispatch, useTypedSelector } from '../../../store/store';
import { PostUserQuestions } from '../../../store/slices/ui';
import { useTrackEvent } from '../../../common/hooks/useTrackEvent';
import { EventNames } from '../../../common/constants/constants';
import { CAV6 } from '../cav6tokens';
import { ProgressDots } from './components/ProgressDots';
import { SpeedBar } from './components/SpeedBar';
import { IdentityBlock } from './blocks/IdentityBlock';
import { QuestionBlock } from './blocks/QuestionBlock';
import { FinalizingBlock } from './blocks/FinalizingBlock';
import { BusinessConfirmBlock, BusinessAnswer } from './blocks/BusinessConfirmBlock';
import { LocationBlock, useShouldAskLocation } from './blocks/LocationBlock';
import { UploadChoiceSheet, UploadChoiceMode } from './UploadChoiceSheet';
import { UploadStatus, UploadTicket } from './UploadTicket';

// Strings matching IncentiveBlock
const Q1 = 'Do you plan on visiting us again?';
const Q2 = 'Would you refer us to friends?';

// Carousel slides, in order. The business step is not a slide: it takes the
// ticket over once the upload is done (see showBusiness).
const STEP = { identity: 0, q1: 1, q2: 2, finalizing: 3 } as const;
const STEP_COUNT = 4;

const BlocksViewport = styled(Box)({
  overflow: 'hidden',
  width: '100%',
});

const BlocksTrack = styled(Box)<{ $stepX: string }>(({ $stepX }) => ({
  display: 'flex',
  width: `${STEP_COUNT * 100}%`,
  transform: `translateX(${$stepX})`,
  transition: `transform ${CAV6.sharedAxisMs}ms ${CAV6.sharedAxisEase}`,
  willChange: 'transform, opacity',
}));

// Every slide is as tall as the tallest (the identity form); shorter steps
// sit centred in that height.
const Block = styled(Box)({
  width: '100%',
  flex: `0 0 ${100 / STEP_COUNT}%`,
  padding: '0 2px',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
});

interface Props {
  /** Called when identity block is submitted. Should trigger auth. Returns true on success. */
  onIdentitySubmit: (data: { firstName: string; lastName: string; phone: string }) => Promise<boolean>;
  /** Called when restart is needed after upload error (handled by parent) */
  onRestartClick?: (trigger?: 'online_event' | 'visibilitychange' | 'retry_timer') => void;
  /** User chose "I'll use Wi-Fi later" on the slow-network choice sheet. */
  onPauseForWifi?: () => void;
  /** User chose "Resume upload" on the paused-for-Wi-Fi sheet. */
  onResumeFromWifi?: () => void;
  initialFirstName?: string;
  initialLastName?: string;
  /** Skip identity block and start at this step (default 0). Use 1 to start at Q1. */
  startAtStep?: number;
}

export const UploadFlowCarousel: FC<Props> = ({
  onIdentitySubmit,
  onRestartClick,
  onPauseForWifi,
  onResumeFromWifi,
  initialFirstName,
  initialLastName,
  startAtStep = 0,
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { trackEvent } = useTrackEvent();
  const brandColor = theme.palette.primary.main;

  const { id: userId } = useTypedSelector((state) => state.me);
  const {
    isUploadInprogress,
    uploadingProgress,
    isResumingUpload,
    isUploadError,
    isUploadFatal,
    etaSeconds,
    isSlowConnection,
    isPausedForWifi,
  } = useTypedSelector((state) => state.upload);
  const { items: questionItems } = useTypedSelector((state) => state.ui.questions);
  const hasAnsweredQuestions = questionItems.length > 1;
  const identityOverlayOpen = useTypedSelector((state) => state.ui.identityOverlayOpen);

  const [stepIndex, setStepIndex] = useState(startAtStep);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const [businessDone, setBusinessDone] = useState(false);
  // Whether the user has already answered the slow-network choice sheet for
  // the CURRENT upload. The offer is one-shot per upload: once someone has
  // seen "continue or switch to Wi-Fi?" and chosen — or dismissed it — asking
  // again mid-upload is an interruption, not a service.
  //
  // This used to re-arm on every slow→fast transition and on every Wi-Fi
  // resume, so a link that oscillated around the threshold could re-prompt
  // repeatedly within a single upload. Combined with thresholds that most
  // cellular uploaders tripped (see `slowConnectionPolicy`), that made the
  // sheet the most-seen surface in the flow. It now resets only when a genuinely
  // new upload starts. The hook holds the matching latch on the trigger side,
  // so both the signal and the surface are one-shot.
  //
  // Note this governs 'choice' mode only — `isPausedForWifi` opens the sheet in
  // 'resume' mode through a separate branch below, which stays available.
  const [choiceSheetDismissed, setChoiceSheetDismissed] = useState(false);
  const prevInProgressRef = useRef(isUploadInprogress);
  useEffect(() => {
    // Leading edge of a new upload — re-arm the offer.
    if (!prevInProgressRef.current && isUploadInprogress) {
      setChoiceSheetDismissed(false);
    }
    prevInProgressRef.current = isUploadInprogress;
  }, [isUploadInprogress]);

  const stepX = `-${(stepIndex * 100) / STEP_COUNT}%`;

  // iOS Safari scrolls the document to lift the focused identity field above
  // the on-screen keyboard, and doesn't reliably restore that scroll once the
  // keyboard dismisses — especially after the carousel translates the focused
  // input off-screen, which left the ticket scrolled part-way. Whenever the
  // step changes, blur any field that's still focused and snap the document
  // back to the top.
  // `behavior:'instant'` overrides the global html{scroll-behavior:smooth} so
  // the reset doesn't visibly drift during the step transition.
  useEffect(() => {
    (document.activeElement as HTMLElement | null)?.blur();
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [stepIndex]);

  // Detect upload completion. Guard against `isPausedForWifi` so a late
  // pause that happens to abort the very last chunk (or that fires *after*
  // CreateStory.fulfilled has already slammed progress to 100 from a stale
  // dispatch) doesn't bounce us into the completion path. Once the user
  // resumes and the upload truly finishes, both conditions hold and the
  // effect promotes uploadComplete on the next tick.
  // Also guard the error/resuming states: when the session watchdog (or an
  // iOS background-kill) tears an upload down, `isUploadInprogress` drops
  // while the displayed progress can still read 100 — promoting completion
  // here painted an "Upload Complete!" banner over an upload that later
  // resumed from a repick. Completion must never be inferred while the
  // machinery is reporting an error or actively resuming.
  useEffect(() => {
    if (
      !isUploadInprogress &&
      uploadingProgress === 100 &&
      !isPausedForWifi &&
      !isUploadError &&
      !isUploadFatal &&
      !isResumingUpload
    ) {
      setUploadComplete(true);
    }
  }, [isUploadInprogress, uploadingProgress, isPausedForWifi, isUploadError, isUploadFatal, isResumingUpload]);

  // Business step: once the upload is done and the identity form is behind
  // the creator (the SignUpScreen overlay closed, the carousel past its own
  // identity slide). If the upload finishes first it follows identity; if not,
  // it takes over from the questions the moment the upload completes. Only in
  // a live attempt: a stale mount after reload has none.
  const liveAttempt = !!getAttempt();
  const showBusiness =
    liveAttempt &&
    uploadComplete &&
    !businessDone &&
    !showComplete &&
    !identityOverlayOpen &&
    stepIndex !== STEP.identity;

  // Show complete screen when both upload is done AND questions are answered.
  // hasAnsweredQuestions comes from the user's saved answers, so it is already
  // true for a returning user; the business step must still be answered first.
  const businessPending = liveAttempt && !businessDone;
  useEffect(() => {
    if (uploadComplete && hasAnsweredQuestions && !businessPending) {
      setShowComplete(true);
    }
  }, [uploadComplete, hasAnsweredQuestions, businessPending]);

  // Keep the screen on while bytes are moving. Off once the upload is done,
  // paused for Wi-Fi, or failed for good. Needs an attempt from this page
  // load: after a reload the persisted isUploadInprogress flag is still true
  // while nothing uploads, which acquired and released a lock in the same
  // instant (QA's reload HAR: acquired → released/unmount, held_ms 0).
  useUploadWakeLock(
    !!getAttempt() && isUploadInprogress && !uploadComplete && !isPausedForWifi && !isUploadFatal,
    trackEvent,
  );

  // Stable ref for trackEvent (needed inside effects with [] deps)
  const trackEventRef = useRef(trackEvent);
  trackEventRef.current = trackEvent;

  // Track complete screen page view
  const completeShownAtRef = useRef<number | null>(null);
  useEffect(() => {
    if (showComplete) {
      // Stale mount from rehydrated state (reload mid-flow): no attempt
      // exists in this page load, so nothing is actually being shown.
      if (!getAttempt()) return;
      completeShownAtRef.current = performance.now();
      setAttemptMeta({ completed: true });
      trackEventRef.current({
        action: EventNames.complete_screen_page_view,
        ms_since_attempt: msSinceAttempt(),
      });
    }
  }, [showComplete]);

  // Track finalizing screen page view
  useEffect(() => {
    if (stepIndex === STEP.finalizing && !showComplete) {
      if (!getAttempt()) return;
      trackEventRef.current({
        action: EventNames.finalizing_screen_page_view,
        waiting_for: !uploadComplete && !hasAnsweredQuestions ? 'both' : !uploadComplete ? 'upload' : 'questions',
        upload_pct: uploadingProgress,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex, showComplete]);

  // Question views (one per step entry).
  const questionShownAtRef = useRef<number | null>(null);
  useEffect(() => {
    if (stepIndex === STEP.q1 || stepIndex === STEP.q2) {
      if (!getAttempt()) return;
      questionShownAtRef.current = performance.now();
      trackEventRef.current({
        action: EventNames.question_view,
        question_index: stepIndex === STEP.q1 ? 1 : 2,
        question_id: stepIndex === STEP.q1 ? 'q1' : 'q2',
        upload_pct: uploadingProgress,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex]);

  // Stage for the single pagehide watch (upload_abandoned / complete_screen_leave).
  const stageRef = useRef({ stepIndex, showComplete, showBusiness, uploadComplete, isPausedForWifi, uploadingProgress });
  stageRef.current = { stepIndex, showComplete, showBusiness, uploadComplete, isPausedForWifi, uploadingProgress };
  useEffect(
    () =>
      registerAbandonStage(() => {
        const st = stageRef.current;
        if (st.showComplete) {
          return {
            stage: 'complete',
            extra: {
              dwell_ms: completeShownAtRef.current ? Math.round(performance.now() - completeShownAtRef.current) : null,
            },
          };
        }
        const step = st.showBusiness
          ? 'business_confirm'
          : st.stepIndex === STEP.identity
          ? 'identity'
          : st.stepIndex <= STEP.q2
          ? 'questions'
          : null;
        const stage = !st.uploadComplete ? (st.isPausedForWifi ? 'paused' : step ?? 'uploading') : step ?? 'finalizing';
        const attempt = getAttempt();
        const bytesDone = attempt?.fileSize ? Math.round((attempt.fileSize * st.uploadingProgress) / 100) : null;
        return {
          stage,
          extra: {
            pct: st.uploadingProgress,
            bytes_done: bytesDone,
            start_offset_pct: attempt?.startOffsetPct ?? null,
            resumes_attempt_id: attempt?.resumesAttemptId ?? null,
          },
        };
      }),
    [],
  );

  // Track upload paused / resumed. `isResumingUpload` also rises during the
  // initial "Preparing your video" window of EVERY upload (URL mint + offset
  // probe, before any byte moves) — QA's monitored session showed a phantom
  // paused→resumed pair ~4.5 s into each fresh upload. Only count a pause when
  // bytes had actually been flowing (progress strictly between 0 and 100), and
  // only count a resume for a pause we counted.
  const prevResumingRef = useRef(false);
  const pauseTrackedRef = useRef(false);
  useEffect(() => {
    const midUpload = uploadingProgress > 0 && uploadingProgress < 100;
    if (isResumingUpload && !prevResumingRef.current && midUpload) {
      pauseTrackedRef.current = true;
      trackEventRef.current({ action: EventNames.upload_paused });
    }
    if (!isResumingUpload && prevResumingRef.current && !isUploadError && pauseTrackedRef.current) {
      pauseTrackedRef.current = false;
      trackEventRef.current({ action: EventNames.upload_resumed });
    }
    prevResumingRef.current = isResumingUpload;
  }, [isResumingUpload, isUploadError, uploadingProgress]);

  // Auto-restart upload when connection recovers
  const onRestartRef = useRef(onRestartClick);
  onRestartRef.current = onRestartClick;
  // Refs to current pause/fatal state so the listeners read the latest value
  // without needing re-attachment on every state change.
  const isPausedForWifiRef = useRef(isPausedForWifi);
  isPausedForWifiRef.current = isPausedForWifi;
  const isUploadFatalRef = useRef(isUploadFatal);
  isUploadFatalRef.current = isUploadFatal;
  // Bytes-done gate for the auto-restart handlers. Once bytes are committed
  // (uploadingProgress === 100), restartUpload short-circuits with undefined
  // (the "already complete" branch), which handleVideoUploadRestart's .then
  // callback used to misread as failure → setUploadError(true) → Dipped
  // banner. Foregrounding the tab post-completion was a reliable trigger.
  const uploadingProgressRef = useRef(uploadingProgress);
  uploadingProgressRef.current = uploadingProgress;
  useEffect(() => {
    const handler = () => {
      // Honor explicit Wi-Fi-later pause — user must tap Resume.
      if (isPausedForWifiRef.current) return;
      // Don't retry a session we already know can't recover.
      if (isUploadFatalRef.current) return;
      // Bytes already up — nothing to restart, and triggering one would
      // cause the post-completion Dipped-banner false alarm.
      if (uploadingProgressRef.current >= 100) return;
      console.log('[UploadResume] online event fired, hasRestart:', !!onRestartRef.current);
      trackEventRef.current({ action: EventNames.upload_network_recovery });
      if (onRestartRef.current) {
        onRestartRef.current('online_event');
      }
    };
    window.addEventListener('online', handler);
    return () => window.removeEventListener('online', handler);
  }, []);

  // On iOS Safari, backgrounding the tab/app aborts in-flight chunk PUTs.
  // When the page returns to the foreground, kick a restart — the existing
  // 308-offset probe in `Upload.getUploadedBytes` will resume cleanly from
  // wherever the server got to. The restart path is idempotent (guarded by
  // `isRestartingRef` + `localStorage.isUploadInProgress`), so this is safe
  // even when nothing was actually aborted.
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState !== 'visible') return;
      if (isPausedForWifiRef.current) return;
      if (isUploadFatalRef.current) return;
      if (uploadingProgressRef.current >= 100) return;
      console.log('[UploadResume] visibilitychange → visible, triggering restart');
      trackEventRef.current({ action: EventNames.upload_network_recovery });
      if (onRestartRef.current) {
        onRestartRef.current('visibilitychange');
      }
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, []);

  // Auto-retry upload after error — keep retrying every 5s while in error state.
  // Bails on fatal (slug guard / post-bytes failure / 410 retry exhausted) so
  // we don't burn cycles on a session that can't recover. Also bails once
  // bytes are committed — see the bytes-done gate above for why.
  useEffect(() => {
    if (!isUploadError) return;
    if (isPausedForWifi) return;
    if (isUploadFatal) return;
    if (uploadingProgress >= 100) return;
    console.log('[UploadResume] Error state detected, will retry every 5s');
    const interval = setInterval(() => {
      console.log('[UploadResume] Retrying upload, navigator.onLine:', navigator.onLine);
      trackEventRef.current({ action: EventNames.upload_retry_attempt });
      if (onRestartRef.current) {
        onRestartRef.current('retry_timer');
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [isUploadError, isPausedForWifi, isUploadFatal, uploadingProgress]);

  const handleIdentityConfirm = useCallback(
    async (data: { firstName: string; lastName: string; phone: string }) => {
      setIsSubmitting(true);
      const success = await onIdentitySubmit(data);
      setIsSubmitting(false);
      if (success) {
        setStepIndex(STEP.q1);
      }
    },
    [onIdentitySubmit],
  );

  // The block already waited out its own tap feedback; the carousel comes back
  // at the step the creator was on (or the complete screen takes over).
  const handleBusinessAnswer = useCallback((_answer: BusinessAnswer) => setBusinessDone(true), []);

  const handleQ1Answer = useCallback(
    (answer: string) => {
      dispatch(PostUserQuestions({ id: userId, answer, question: Q1 }));
      trackEvent({ action: EventNames.question_1, value: answer });
      trackEvent({
        action: EventNames.question_answered,
        question_index: 1,
        question_id: 'q1',
        value: answer,
        ms_since_view: questionShownAtRef.current ? Math.round(performance.now() - questionShownAtRef.current) : null,
      });
      setTimeout(() => setStepIndex(STEP.q2), 400);
    },
    [dispatch, userId, trackEvent],
  );

  const handleQ2Answer = useCallback(
    (answer: string) => {
      dispatch(PostUserQuestions({ id: userId, answer, question: Q2 }));
      trackEvent({ action: EventNames.question_2, value: answer });
      trackEvent({
        action: EventNames.question_answered,
        question_index: 2,
        question_id: 'q2',
        value: answer,
        ms_since_view: questionShownAtRef.current ? Math.round(performance.now() - questionShownAtRef.current) : null,
      });
      trackEvent({ action: EventNames.questions_complete, total_ms: msSinceAttempt() });
      setTimeout(() => setStepIndex(STEP.finalizing), 400);
    },
    [dispatch, userId, trackEvent],
  );

  const showSpeedBar = isUploadInprogress && !showComplete;
  const showDots = stepIndex <= STEP.q2 && !showComplete;

  // Upload-choice sheet: open in 'choice' mode when slow-network detected and
  // user hasn't already chosen Continue; open in 'resume' mode when paused-
  // for-Wi-Fi. Closes immediately on bytes-done (the offer to "use Wi-Fi
  // later" stops being meaningful once there's nothing left to upload).
  const sheetMode: UploadChoiceMode = isPausedForWifi ? 'resume' : 'choice';
  const sheetOpen =
    !showComplete &&
    !uploadComplete &&
    (isPausedForWifi || (isSlowConnection && !choiceSheetDismissed && !isResumingUpload && !isUploadError));

  const etaLabel =
    etaSeconds != null && etaSeconds > 0
      ? etaSeconds < 90
        ? `${Math.max(5, Math.round(etaSeconds / 5) * 5)} seconds`
        : etaSeconds < 3600
        ? `${Math.round(etaSeconds / 60)} minutes`
        : 'a long time'
      : null;
  const sheetBodyOverride =
    sheetMode === 'choice' && etaLabel
      ? `Estimated ${etaLabel} on this connection speed. Continue upload now, or save progress and resume on Wi-Fi.`
      : undefined;

  // Refs so the close handler can include mode/eta in the dismiss event
  // without taking them as deps (they change every chunk).
  const sheetModeRef = useRef(sheetMode);
  sheetModeRef.current = sheetMode;
  const sheetEtaRef = useRef<number | null>(etaSeconds);
  sheetEtaRef.current = etaSeconds;
  const sheetMbpsRef = useRef<number | undefined>(undefined);
  // Pull current smoothed Mbps from ui slice's uploadMetrics
  const speedMbsForTelemetry = useTypedSelector((state) => state.ui.uploadMetrics.speedMbs);
  sheetMbpsRef.current = speedMbsForTelemetry;

  // Fire choice-sheet view event once per open. Includes mode + ETA + Mbps so
  // dashboards can split "saw the warning at 5min ETA" vs "saw it at 100s ETA",
  // and disambiguate slow-network vs in-session Wi-Fi-pause views.
  const sheetSeenRef = useRef(false);
  const sheetOpenedAtRef = useRef<number | null>(null);
  const sheetViewCountRef = useRef(0);
  const wifiPausedAtRef = useRef<number | null>(null);
  useEffect(() => {
    if (sheetOpen && !sheetSeenRef.current) {
      if (!getAttempt()) return;
      sheetSeenRef.current = true;
      sheetOpenedAtRef.current = performance.now();
      trackEventRef.current({
        action: EventNames.upload_choice_sheet_view,
        mode: sheetMode,
        etaSeconds: etaSeconds ?? undefined,
        speedMbpsEwma: speedMbsForTelemetry || undefined,
      });
      if (sheetMode === 'choice') {
        sheetViewCountRef.current += 1;
        trackEventRef.current({
          action: EventNames.slow_connection_sheet_view,
          measured_mbps: speedMbsForTelemetry || null,
          threshold_mbps: SLOW_MBPS_THRESHOLD,
          eta_s: etaSeconds ?? null,
          file_size: getAttempt()?.fileSize ?? null,
          pct_done: uploadingProgress,
          trigger: uploadingProgress > 0 ? 'live_ewma' : 'landing_test',
          view_count: sheetViewCountRef.current,
        });
      }
    } else if (!sheetOpen) {
      sheetSeenRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheetOpen, sheetMode, etaSeconds, speedMbsForTelemetry]);

  const sheetOpenMs = () => (sheetOpenedAtRef.current ? Math.round(performance.now() - sheetOpenedAtRef.current) : null);

  const handleSheetContinue = useCallback(() => {
    trackEventRef.current({ action: EventNames.upload_continue_clicked });
    trackEventRef.current({
      action: EventNames.slow_connection_sheet_action,
      sheet_action: 'continue',
      ms_open: sheetOpenMs(),
    });
    setChoiceSheetDismissed(true);
  }, []);

  const handleSheetWifiLater = useCallback(() => {
    trackEventRef.current({ action: EventNames.upload_wifi_later_clicked });
    trackEventRef.current({
      action: EventNames.slow_connection_sheet_action,
      sheet_action: 'wifi_later',
      ms_open: sheetOpenMs(),
    });
    wifiPausedAtRef.current = performance.now();
    setChoiceSheetDismissed(true);
    onPauseForWifi?.();
  }, [onPauseForWifi]);

  const handleSheetResume = useCallback(() => {
    trackEventRef.current({ action: EventNames.upload_resume_wifi_clicked });
    trackEventRef.current({
      action: EventNames.wifi_later_resume_click,
      waited_ms: wifiPausedAtRef.current ? Math.round(performance.now() - wifiPausedAtRef.current) : null,
      mbps_now: sheetMbpsRef.current || null,
    });
    onResumeFromWifi?.();
  }, [onResumeFromWifi]);

  const handleSheetClose = useCallback(() => {
    // Track dismissal so we can measure "saw warning, ignored it" vs the
    // explicit Continue / Wi-Fi-later actions.
    trackEventRef.current({
      action: EventNames.upload_choice_sheet_dismissed,
      mode: sheetModeRef.current,
      etaSeconds: sheetEtaRef.current ?? undefined,
      speedMbpsEwma: sheetMbpsRef.current || undefined,
    });
    if (sheetModeRef.current === 'choice') {
      trackEventRef.current({
        action: EventNames.slow_connection_sheet_action,
        sheet_action: 'dismiss',
        ms_open: sheetOpenMs(),
      });
    }
    setChoiceSheetDismissed(true);
  }, []);

  const [askLocation, hideLocationAsk] = useShouldAskLocation(showComplete && !!getAttempt());

  // Interruption / Wi-Fi pause / failure notices and "Upload Complete!" are
  // shown by UploadStatus at the top of the ticket.
  return (
    <>
      <SpeedBar visible={showSpeedBar} brandColor={brandColor} />

      <UploadTicket status={<UploadStatus complete={uploadComplete} thanks={showComplete} />}>
        {showComplete ? (
          askLocation ? (
            <LocationBlock onSkip={hideLocationAsk} />
          ) : undefined
        ) : showBusiness ? (
          <BusinessConfirmBlock isActive onAnswer={handleBusinessAnswer} />
        ) : (
          <>
            <BlocksViewport>
              <BlocksTrack $stepX={stepX}>
                <Block>
                  <IdentityBlock
                    isActive={stepIndex === STEP.identity}
                    onConfirm={handleIdentityConfirm}
                    initialFirstName={initialFirstName}
                    initialLastName={initialLastName}
                    isSubmitting={isSubmitting}
                  />
                </Block>
                <Block>
                  <QuestionBlock question={Q1} onAnswer={handleQ1Answer} />
                </Block>
                <Block>
                  <QuestionBlock question={Q2} onAnswer={handleQ2Answer} />
                </Block>
                <Block>
                  <FinalizingBlock />
                </Block>
              </BlocksTrack>
            </BlocksViewport>

            {showDots && <ProgressDots count={STEP.q2 + 1} activeIndex={stepIndex} />}
          </>
        )}
      </UploadTicket>

      <UploadChoiceSheet
        open={sheetOpen}
        mode={sheetMode}
        bodyOverride={sheetBodyOverride}
        brandColor={brandColor}
        onContinue={handleSheetContinue}
        onWifiLater={handleSheetWifiLater}
        onResume={handleSheetResume}
        onClose={handleSheetClose}
      />
    </>
  );
};
